import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashIdentity from 'lodash/fp/identity';

import isEmail from 'common/util/isEmail';
import addNumberPronounSuffix from 'common/util/addNumberPronounSuffix';

import { SCREEN_ID_HOUSEHOLD_COLLECT_ROOMMATE_INFO } from 'client/constants/screenIds';

import log from 'client/lib/log';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLastLocation from 'client/helpers/withLastLocation';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import InputText from 'client/components/InputText';
import HouseholdActions from 'client/actions/Household';
import Navbar from 'client/components/Navbar';
import ActivityIndicator from 'client/components/ActivityIndicator';
import BackButton from 'client/components/BackButton';

import branch from 'client/core/branch';

const debug = log.debug('sunroom:pages:HouseholdCollectRoommateInfo');

@withTransitionHook
@withScrollToTop
@withLastLocation
@branch({
  currentUser: ['currentUser'],
  roommateApplication: ['roommateApplication'],
})
@withScreenId(SCREEN_ID_HOUSEHOLD_COLLECT_ROOMMATE_INFO)
class HouseholdCollectRoommateInfo extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Invite Roommates to Apply | Sunroom Rentals',
    section: 'Roommate Info',
  });

  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    lastLocation: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this._onConfirmInfoFormSubmit = this._onConfirmInfoFormSubmit.bind(this);

    const roommatesToSubmit = new Array(props.roommateApplication.count);

    this.state = {
      nameInput: '',
      phoneInput: '',
      emailInput: '',
      roommatesToSubmit: roommatesToSubmit,
      roommateIndex: 0,
      isSubmitting: false,
      errorMessage: '',
    };
  }

  async _onConfirmInfoFormSubmit(e) {
    e.preventDefault();

    const { match, dispatch, roommateApplication, currentUser } = this.props;

    const { roommatesToSubmit, roommateIndex, nameInput, phoneInput, emailInput } = this.state;

    const roommatesToCheck = roommatesToSubmit
      .filter((roommate, index) => index !== roommateIndex)
      .filter(lodashIdentity);

    if (roommatesToCheck.some(({ phone }) => phone === phoneInput)) {
      this.setState({
        errorMessage:
          'Whoops, you already added a roommate with this phone number. ' +
          'Please enter a new phone number.',
      });
      return;
    }

    if (phoneInput === currentUser.phone) {
      this.setState({
        errorMessage: "Whoops, this is your phone. Please enter your roommate's phone.",
      });
      return;
    }

    if (emailInput && roommatesToCheck.some(({ email }) => email === emailInput)) {
      this.setState({
        errorMessage:
          'Whoops, you already added a roommate with this email. Please enter a new email.',
      });
      return;
    }

    const copyOfRoommatesToSubmit = [...roommatesToSubmit];
    const newRoommate = {
      name: nameInput,
      phone: phoneInput,
      email: emailInput,
    };

    copyOfRoommatesToSubmit[roommateIndex] = newRoommate;
    const newRoommateIndex = roommateIndex + 1;
    const checkReadyToSubmit =
      newRoommateIndex !== 0 && newRoommateIndex === roommateApplication.count;

    this.setState({
      roommateIndex: newRoommateIndex,
      roommatesToSubmit: copyOfRoommatesToSubmit,
      nameInput: '',
      phoneInput: '',
      emailInput: '',
      isSubmitting: checkReadyToSubmit,
      errorMessage: '',
    });

    debug('Added roommate', {
      newRoommateIndex,
      roommateApplication,
      copyOfRoommatesToSubmit,
    });

    // The last roommate in the array.
    if (newRoommateIndex === roommateApplication.count) {
      await dispatch(HouseholdActions.roommateApplicationStep, {
        tourId: parseInt(match.params.tourId, 10),
        listingId: parseInt(match.params.listingId, 10),
        roommatePayload: {
          roommates: copyOfRoommatesToSubmit,
        },
      });
    }
  }

  _onConfirmNameInputChange = (event) => {
    this.setState({
      nameInput: event.target.value || '',
    });
  };

  _onConfirmPhoneInputChange = (event) => {
    this.setState({
      phoneInput:
        event && !isNaN(Number(event.target.value)) && event.target.value.length <= 10
          ? event.target.value
          : this.state.phoneInput,
    });
  };

  _onConfirmEmailInputChange = (event) => {
    this.setState({
      emailInput: event.target.value || '',
    });
  };

  _onClickBackButton = (event) => {
    event.preventDefault();
    const { lastLocation, history } = this.props;
    const { roommateIndex, roommatesToSubmit } = this.state;

    if (roommateIndex === 0) {
      history.push((lastLocation && lastLocation.pathname) || '/tour');
    } else {
      const previousRoommate = roommatesToSubmit[roommateIndex - 1];
      this.setState({
        nameInput: previousRoommate.name,
        phoneInput: previousRoommate.phone,
        emailInput: previousRoommate.email,
        roommateIndex: roommateIndex - 1,
      });
    }
  };

  renderForm() {
    const { roommateApplication } = this.props;

    const { roommateIndex, nameInput, phoneInput, emailInput, errorMessage } = this.state;

    const isDisabled = phoneInput.length !== 10 || nameInput === '' || !isEmail(emailInput);

    const headline =
      roommateApplication.count > 1
        ? `Invite your ${addNumberPronounSuffix(roommateIndex + 1)} roommate`
        : 'Invite your roommate';

    if (!roommateApplication.count) {
      return <Redirect to="/tour" />;
    }

    return (
      <div className="inviteRoommates">
        <Navbar leftButton={<BackButton onClick={this._onClickBackButton} />} />
        <div className="stepContainer">
          <div className="textContainer">
            <h1>{headline}</h1>
          </div>
          <div className="formContainer answers">
            <div>
              <div className="roommateInfoLabelContainer">
                <span className="roommateInfoLabel">full name</span>
              </div>
              {errorMessage && (
                <div className="formMessage error">
                  <span>{errorMessage}</span>
                </div>
              )}
              <InputText
                placeholder="John Smith"
                onChange={this._onConfirmNameInputChange}
                type="text"
                value={nameInput || ''}
                name="roommateInfoName"
              />
            </div>
            <div>
              <div className="roommateInfoLabelContainer">
                <span className="roommateInfoLabel">phone</span>
              </div>
              <InputText
                placeholder="5121234567"
                onChange={this._onConfirmPhoneInputChange}
                type="tel"
                pattern="[0-9]*"
                inputmode="numeric"
                value={phoneInput || ''}
                name="roommateInfoPhone"
              />
            </div>
            <div>
              <div className="roommateInfoLabelContainer">
                <span className="roommateInfoLabel">email</span>
              </div>
              <InputText
                placeholder="Write email here..."
                onChange={this._onConfirmEmailInputChange}
                type="text"
                value={emailInput || ''}
                name="roommateInfoEmail"
              />
            </div>
            <ButtonDeprecated
              className="invite"
              type="submit"
              onClick={this._onConfirmInfoFormSubmit}
              disabled={isDisabled}
              name="roommateInfoSubmit"
            >
              invite roommate
            </ButtonDeprecated>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isSubmitting } = this.state;

    return (
      <div className="page-inviteRoommates">
        {isSubmitting ? <ActivityIndicator /> : this.renderForm()}
      </div>
    );
  }
}

HouseholdCollectRoommateInfo.displayName = 'HouseholdCollectRoommateInfo';

export default HouseholdCollectRoommateInfo;
